/**
 * Minimal ZIP writer. Used for save-slot exports, which are ~1MB, so we build
 * the archive as a Blob rather than streaming it. Deflate via CompressionStream
 * when available; otherwise entries are stored uncompressed (still a valid zip).
 */

/** TS 5.7 splits Uint8Array by backing-buffer type; Blob only accepts the
 * ArrayBuffer flavour. The runtime never sees a SharedArrayBuffer here. */
const part = (u: Uint8Array): BlobPart => u as unknown as BlobPart;

let CRC_TABLE: Uint32Array | null = null;
function crcTable(): Uint32Array {
  if (CRC_TABLE) return CRC_TABLE;
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  CRC_TABLE = t;
  return t;
}

export function crc32(bytes: Uint8Array): number {
  const t = crcTable();
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = t[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

async function deflateRaw(bytes: Uint8Array): Promise<Uint8Array | null> {
  if (typeof CompressionStream === 'undefined') return null;
  try {
    const cs = new CompressionStream('deflate-raw');
    const out = new Response(new Blob([part(bytes)]).stream().pipeThrough(cs));
    return new Uint8Array(await out.arrayBuffer());
  } catch {
    return null;
  }
}

function dosDateTime(d: Date): { time: number; date: number } {
  const time = (d.getHours() << 11) | (d.getMinutes() << 5) | (Math.floor(d.getSeconds() / 2) & 0x1f);
  const date = ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  return { time, date };
}

export interface ZipInput {
  name: string;
  data: Uint8Array;
  lastModified?: number;
}

export async function createZip(inputs: ZipInput[]): Promise<Blob> {
  const enc = new TextEncoder();
  const parts: BlobPart[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;

  for (const input of inputs) {
    const nameBytes = enc.encode(input.name);
    const crc = crc32(input.data);
    const deflated = input.data.length > 64 ? await deflateRaw(input.data) : null;
    const useDeflate = deflated !== null && deflated.length < input.data.length;
    const payload = useDeflate ? deflated! : input.data;
    const method = useDeflate ? 8 : 0;
    const { time, date } = dosDateTime(new Date(input.lastModified ?? Date.now()));

    const lh = new DataView(new ArrayBuffer(30));
    lh.setUint32(0, 0x04034b50, true);
    lh.setUint16(4, 20, true);
    lh.setUint16(6, 0, true);
    lh.setUint16(8, method, true);
    lh.setUint16(10, time, true);
    lh.setUint16(12, date, true);
    lh.setUint32(14, crc, true);
    lh.setUint32(18, payload.length, true);
    lh.setUint32(22, input.data.length, true);
    lh.setUint16(26, nameBytes.length, true);
    lh.setUint16(28, 0, true);

    parts.push(part(new Uint8Array(lh.buffer)), part(nameBytes), part(payload));

    const cd = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(cd.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, 0, true);
    cv.setUint16(10, method, true);
    cv.setUint16(12, time, true);
    cv.setUint16(14, date, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, payload.length, true);
    cv.setUint32(24, input.data.length, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint32(42, offset, true);
    cd.set(nameBytes, 46);
    central.push(cd);

    offset += 30 + nameBytes.length + payload.length;
  }

  const cdSize = central.reduce((n, c) => n + c.length, 0);
  const eocd = new DataView(new ArrayBuffer(22));
  eocd.setUint32(0, 0x06054b50, true);
  eocd.setUint16(8, central.length, true);
  eocd.setUint16(10, central.length, true);
  eocd.setUint32(12, cdSize, true);
  eocd.setUint32(16, offset, true);

  return new Blob([...parts, ...central.map(part), part(new Uint8Array(eocd.buffer))], { type: 'application/zip' });
}
